import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../utils/api';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#3F51B5',
    borderRadius: '8px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }
};

/* ─── Inline SetupIntent form ──────────────────────────────────── */
const SetupForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required'
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
    } else if (setupIntent && setupIntent.status === 'succeeded') {
      onSuccess(setupIntent.id);
    } else if (setupIntent && setupIntent.status === 'processing') {
      // Bank account micro-deposit flow
      setError('Bank verification in progress. You may need to verify micro-deposits sent to your account.');
      setProcessing(false);
    } else {
      setError('Something went wrong. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <p className="text-sm text-red-600 mt-3">{error}</p>
      )}
      <div className="flex gap-3 mt-5">
        <button
          type="submit"
          disabled={!stripe || processing}
          className="bg-brand-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {processing ? 'Saving...' : 'Save Payment Method'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-600 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const STATUS_COLORS = {
  succeeded: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-700',
  processing: 'bg-blue-100 text-blue-700'
};

const ENROLLMENT_STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  payment_failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  completed: 'bg-blue-100 text-blue-700',
  pending_payment: 'bg-yellow-100 text-yellow-700'
};

const MyPayments = () => {
  const [payments, setPayments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Payment method update state
  const [currentMethod, setCurrentMethod] = useState(null);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState('');
  const [updateError, setUpdateError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [paymentsRes, enrollmentsRes, pmRes, meRes] = await Promise.all([
          api.get('/payments/my'),
          api.get('/enroll/my'),
          api.get('/enroll/current-payment-method').catch(() => ({ data: {} })),
          api.get('/auth/me').catch(() => ({ data: {} }))
        ]);
        setPayments(paymentsRes.data.payments || []);
        setEnrollments(enrollmentsRes.data.enrollments || []);
        if (pmRes.data.paymentMethod) setCurrentMethod(pmRes.data.paymentMethod);
        setMe(meRes.data?.user || null);
      } catch (err) {
        console.error('Failed to load payment data:', err.message);
        setError('Failed to load payment information');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleStartUpdate = async () => {
    setUpdateError('');
    setUpdateSuccess('');
    try {
      const { data } = await api.post('/enroll/setup-intent');
      setSetupClientSecret(data.clientSecret);
      setShowUpdateForm(true);
    } catch (err) {
      setUpdateError(err.response?.data?.error || 'Failed to start payment method update');
    }
  };

  const handleSetupSuccess = async (setupIntentId) => {
    try {
      const { data } = await api.post('/enroll/update-payment-method', { setupIntentId });
      setCurrentMethod(data.paymentMethod);
      setShowUpdateForm(false);
      setSetupClientSecret(null);
      setUpdateSuccess(
        `Payment method updated to ${data.paymentMethod.brand} ending in ${data.paymentMethod.last4}. ` +
        `${data.subscriptionsUpdated} subscription${data.subscriptionsUpdated !== 1 ? 's' : ''} updated.`
      );
    } catch (err) {
      setUpdateError(err.response?.data?.error || 'Failed to update payment method');
    }
  };

  const handleCancelUpdate = () => {
    setShowUpdateForm(false);
    setSetupClientSecret(null);
  };

  // Compute summary stats
  const succeededPayments = payments.filter(p => p.status === 'succeeded');
  const totalPaid = succeededPayments.reduce((sum, p) => sum + (p.totalCharged || 0), 0);

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const activeSubscriptions = activeEnrollments.filter(
    e => e.billingPlan?.frequency && e.billingPlan.frequency !== 'one_time'
  );

  // Find the nearest next payment date from subscriptions
  const nextPaymentDate = activeSubscriptions
    .map(e => e.currentPeriodEnd)
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b))[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatPeriod = (start, end) => {
    if (!start || !end) return '--';
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${new Date(start).toLocaleDateString('en-US', opts)} - ${new Date(end).toLocaleDateString('en-US', opts)}`;
  };

  const formatFrequency = (freq) => {
    const map = { monthly: '/mo', quarterly: '/qtr', semester: '/sem', annual: '/yr', one_time: '' };
    return map[freq] || '';
  };

  const formatEnrollmentStatus = (status) => {
    const map = {
      active: 'Active',
      payment_failed: 'Payment Failed',
      cancelled: 'Cancelled',
      completed: 'Completed',
      pending_payment: 'Pending'
    };
    return map[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span className="ml-3 text-gray-500">Loading payments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Payments</h1>

      {/* Payment Method Section */}
      {activeSubscriptions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Payment Method</h2>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            {currentMethod ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  {currentMethod.type === 'bank' ? (
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21" /></svg>
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {currentMethod.brand} ending in {currentMethod.last4}
                    </p>
                    <p className="text-xs text-gray-500">
                      {currentMethod.type === 'bank' ? 'Bank account (ACH)' : 'Credit/debit card'}
                    </p>
                  </div>
                </div>
                {!showUpdateForm && (
                  <button
                    onClick={handleStartUpdate}
                    className="text-brand-500 text-sm font-medium hover:text-brand-600 transition-colors"
                  >
                    Update Payment Method
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-gray-500">No payment method on file</p>
                {!showUpdateForm && (
                  <button
                    onClick={handleStartUpdate}
                    className="text-brand-500 text-sm font-medium hover:text-brand-600 transition-colors"
                  >
                    Add Payment Method
                  </button>
                )}
              </div>
            )}

            {updateSuccess && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700">{updateSuccess}</p>
              </div>
            )}

            {updateError && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{updateError}</p>
              </div>
            )}

            {showUpdateForm && setupClientSecret && stripePromise && (
              <Elements stripe={stripePromise} options={{ clientSecret: setupClientSecret, appearance: STRIPE_APPEARANCE }}>
                <SetupForm onSuccess={handleSetupSuccess} onCancel={handleCancelUpdate} />
              </Elements>
            )}
          </div>
        </div>
      )}

      {/* Active Courses */}
      {activeSubscriptions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Active Courses</h2>
          <div className="space-y-3">
            {activeSubscriptions.map(enrollment => (
              <div
                key={enrollment._id}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <div>
                  <h3 className="font-medium text-gray-900">{enrollment.course?.name || 'Course'}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    ${enrollment.billingPlan?.amount?.toFixed(2)}{formatFrequency(enrollment.billingPlan?.frequency)}
                    {enrollment.billingPlan?.name ? ` — ${enrollment.billingPlan.name}` : ''}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Current period: {formatPeriod(enrollment.currentPeriodStart, enrollment.currentPeriodEnd)}
                  </p>
                </div>
                <span className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-medium ${ENROLLMENT_STATUS_COLORS[enrollment.status] || 'bg-gray-100 text-gray-500'}`}>
                  {formatEnrollmentStatus(enrollment.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* One-time / non-subscription active enrollments */}
      {activeEnrollments.filter(e => !e.billingPlan?.frequency || e.billingPlan.frequency === 'one_time').length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Active Enrollments</h2>
          <div className="space-y-3">
            {activeEnrollments
              .filter(e => !e.billingPlan?.frequency || e.billingPlan.frequency === 'one_time')
              .map(enrollment => (
                <div
                  key={enrollment._id}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">{enrollment.course?.name || 'Course'}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      ${enrollment.billingPlan?.amount?.toFixed(2)} — One-time
                    </p>
                  </div>
                  <span className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-medium ${ENROLLMENT_STATUS_COLORS[enrollment.status] || 'bg-gray-100 text-gray-500'}`}>
                    {formatEnrollmentStatus(enrollment.status)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Payment History Table */}
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Payment History</h2>
      {payments.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-500">
          No payments yet
        </div>
      ) : (() => {
        const myId = (me?.id || me?._id || '').toString();
        const showFor = payments.some(p => {
          const sid = (p.student?._id || p.student || '').toString();
          return sid && sid !== myId;
        });
        return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Course</th>
                  {showFor && (
                    <th className="text-left px-4 py-3 font-medium text-gray-600">For</th>
                  )}
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Amount</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payments.map(p => {
                  const sid = (p.student?._id || p.student || '').toString();
                  const forSelf = !sid || sid === myId;
                  return (
                  <tr key={p._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">
                      {formatDate(p.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {p.course?.name || '--'}
                    </td>
                    {showFor && (
                      <td className="px-4 py-3 text-gray-700">
                        {forSelf ? <span className="text-gray-400">You</span> : (p.student?.name || '--')}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right text-gray-900 font-medium">
                      ${p.totalCharged?.toFixed(2) || p.amount?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-500'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 capitalize">
                      {p.type?.replace('_', ' ') || '--'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default MyPayments;
